import { apiClient } from '../apiClient';

/**
 * Get linked tenants (public endpoint, no company needed)
 */
export async function getLinkedTenants(): Promise<{ tenants: Array<{ tenantId: string; name: string; logo: string | null; status: string; role: string }> }> {
  // Migrated to RPC: POST /api/rpc/tenants/get-my-tenants
  const { data } = await apiClient.post(`/api/rpc/tenants/get-my-tenants`, {}, {
    timeout: 10000, // 10 seconds - shorter timeout for this non-critical call
  });
  return data;
}

/**
 * Switch tenant (public endpoint, no company needed)
 */
export async function switchTenant(payload: { tenantId: string }): Promise<{ token: string }> {
  // Migrated to RPC: POST /api/rpc/tenants/switch-tenant
  const { data } = await apiClient.post(`/api/rpc/tenants/switch-tenant`, payload);
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Helper to build RPC home base URL
 * CRITICAL: Validates company before building URL to prevent 404s
 */
const homeRpcBase = (company: string): string => {
  // CRITICAL FIX: Validate company parameter
  if (!company || company === 'undefined' || company.trim() === '') {
    const error = new Error('Company slug is required but not available. Please ensure you are on a valid company page.');
    console.error('[home] homeRpcBase failed:', error.message, {
      providedCompany: company,
    });
    throw error; // Throw error to prevent malformed request
  }
  return `/api/${company}/rpc/home`;
};

/**
 * Get home catalog with retry logic
 */
export async function getHomeCatalog(company: string, retries = 2): Promise<any[]> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await apiClient.post(`${homeRpcBase(company)}/catalog`, {}, {
        timeout: 30000, // Increased to 30 seconds to handle slower connections
      });
      const { data } = response;
      if (!data?.ok) {
        const error = new Error(data?.error || 'Failed to get home catalog');
        throw error;
      }
      const result = data.data || [];
      return result;
    } catch (err: any) {
      // If it's the last attempt or not a timeout error, throw
      if (attempt === retries || (err?.code !== 'ECONNABORTED' && !err?.message?.includes('timeout'))) {
        throw err;
      }
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
  const finalError = new Error('Failed to get home catalog after retries');
  throw finalError;
}

/**
 * Get home preferences with retry logic
 */
export async function getHomePrefs(company: string, retries = 2): Promise<{
  pinned: string[];
  hidden: string[];
  order: string[];
  version: number;
}> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await apiClient.post(`${homeRpcBase(company)}/get-prefs`, {}, {
        timeout: 30000, // Increased to 30 seconds to handle slower connections
      });
      const { data } = response;
      if (!data?.ok) {
        const error = new Error(data?.error || 'Failed to get home preferences');
        throw error;
      }
      const result = data.data || { pinned: [], hidden: [], order: [], version: 1 };
      return result;
    } catch (err: any) {
      // If it's the last attempt or not a timeout error, throw
      if (attempt === retries || (err?.code !== 'ECONNABORTED' && !err?.message?.includes('timeout'))) {
        throw err;
      }
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
  const finalError = new Error('Failed to get home preferences after retries');
  throw finalError;
}

/**
 * Save home preferences
 */
export async function saveHomePrefs(
  company: string,
  prefs: {
    pinned: string[];
    hidden: string[];
    order: string[];
    version: number;
  }
): Promise<{
  pinned: string[];
  hidden: string[];
  order: string[];
  version: number;
}> {
  const { data } = await apiClient.post(`${homeRpcBase(company)}/update-prefs`, prefs, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to save home preferences');
  }
  return data.data;
}

/**
 * Get home metrics
 */
export async function getHomeMetrics(company: string): Promise<any> {
  const { data } = await apiClient.post(`${homeRpcBase(company)}/metrics`, {}, {
    timeout: 10000, // Increased from 5000ms to match other home endpoints
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get home metrics');
  }
  return data.data;
}

